import { Loading } from "../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  addWorkdays,
  COMPONENT_STATUS_LABELS,
  COMPONENT_STATUSES,
  INCIDENT_KIND_LABELS,
  INCIDENT_PHASE_LABELS,
  INCIDENT_PHASES,
  INCIDENT_STATE_LABELS,
  MAINTENANCE_NOTICE_WORKDAYS,
  OVERALL_STATUS_LABELS,
  STATUS_COMPONENTS,
  type ComponentStatus,
  type IncidentDto,
  type IncidentKind,
  type IncidentPhase,
  type IncidentSeverity,
  type StatusComponentKey,
  type StatusResponse,
} from "@klantpunt/shared";
import { IconPlus } from "../app/icons";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { errMsg, fmtDateTime } from "../features/support/api";
import { statusBannerKey } from "../features/status/StatusBanner";
import "../features/status/status.css";
import { SupportGuard } from "./SupportInbox";

const adminKey = ["status", "admin"] as const;

/** Statuspagina beheren (docs/HELPCENTER.md §7.2): onderdelen, storingen en gepland onderhoud. */
export function StatusAdminPage() {
  return (
    <SupportGuard>
      <StatusAdmin />
    </SupportGuard>
  );
}

function useStatusAdmin() {
  return useQuery({ queryKey: adminKey, queryFn: () => api<StatusResponse>("/status/admin") });
}

function StatusAdmin() {
  const q = useStatusAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState<IncidentKind | null>(null);
  const s = q.data;
  const refresh = (d?: StatusResponse) => {
    if (d) qc.setQueryData(adminKey, d);
    void qc.invalidateQueries({ queryKey: statusBannerKey });
  };

  async function setManual(id: string, status: ComponentStatus) {
    try {
      refresh(await api<StatusResponse>(`/status/components/${id}`, { method: "PUT", body: { status } }));
      toast("Status bijgewerkt");
    } catch (e) {
      toast(errMsg(e));
    }
  }

  return (
    <div className="page st-admin">
      <p className="label">
        <Link to="/platform">Platformbeheer</Link> · <Link to="/platform/support">Support</Link>
      </p>
      <div className="titlerow">
        <h1 className="dot">Status</h1>
        <div className="btnrow">
          <button type="button" className="btn" onClick={() => setCreating("maintenance")}>
            Onderhoud plannen
          </button>
          <button type="button" className="btn primary" onClick={() => setCreating("incident")}>
            <IconPlus size={20} aria-hidden="true" />
            Storing melden
          </button>
        </div>
      </div>
      {q.isPending && <Loading />}
      {s && (
        <>
          <p className={`stline st-${s.overall}`}>
            <span className="stdot" aria-hidden="true" />
            {OVERALL_STATUS_LABELS[s.overall]}
          </p>
          {creating && (
            <NewIncident
              kind={creating}
              onClose={() => setCreating(null)}
              onSaved={(d) => {
                refresh(d);
                setCreating(null);
              }}
            />
          )}

          <section className="panel" aria-labelledby="comps-h">
            <h2 id="comps-h">Onderdelen</h2>
            <p className="muted small">
              De status volgt vanzelf uit lopende meldingen. Zet hier alleen iets handmatig als er geen melding bij
              hoort.
            </p>
            <ul className="st-comps">
              {s.components.map((c) => (
                <li key={c.id}>
                  <span className="grow">{c.name}</span>
                  <span className={`chip st-${c.status}`}>{COMPONENT_STATUS_LABELS[c.status]}</span>
                  <span className="muted small">{c.uptime90.toFixed(2)}% (90 dagen)</span>
                  <select
                    aria-label={`Handmatige status ${c.name}`}
                    value={c.manual}
                    onChange={(e) => void setManual(c.id, e.target.value as ComponentStatus)}
                  >
                    {COMPONENT_STATUSES.map((st) => (
                      <option key={st} value={st}>
                        {COMPONENT_STATUS_LABELS[st]}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </section>

          <IncidentList title="Lopend" empty="Er loopt nu niets." items={s.active} onSaved={refresh} />
          <IncidentList
            title="Gepland onderhoud"
            empty="Geen onderhoud gepland."
            items={s.scheduled}
            onSaved={refresh}
          />
          <IncidentList title="Afgelopen 90 dagen" empty="Nog niets opgelost." items={s.past} onSaved={refresh} />
        </>
      )}
    </div>
  );
}

const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function NewIncident({
  kind,
  onClose,
  onSaved,
}: {
  kind: IncidentKind;
  onClose: () => void;
  onSaved: (d: StatusResponse) => void;
}) {
  const toast = useToast();
  const maintenance = kind === "maintenance";
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<IncidentSeverity>("degraded");
  const [components, setComponents] = useState<StatusComponentKey[]>([]);
  const [showBanner, setShowBanner] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [body, setBody] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tooSoon = maintenance && start && new Date(start) < addWorkdays(new Date(), MAINTENANCE_NOTICE_WORKDAYS);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ id: string; status: StatusResponse }>("/status/incidents", {
        method: "POST",
        body: {
          incident: {
            kind,
            title,
            severity: maintenance ? null : severity,
            components,
            showBanner,
            scheduledStart: maintenance && start ? new Date(start).toISOString() : null,
            scheduledEnd: maintenance && end ? new Date(end).toISOString() : null,
          },
          update: { phase: "investigating", body, notify },
        },
      });
      toast(maintenance ? "Onderhoud gepland" : "Storing gemeld");
      onSaved(r.status);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (k: StatusComponentKey, on: boolean) =>
    setComponents((cs) => (on ? [...cs, k] : cs.filter((c) => c !== k)));

  return (
    <form className="panel" onSubmit={(e) => void submit(e)} aria-labelledby="new-h">
      <h2 id="new-h">{maintenance ? "Onderhoud plannen" : "Storing melden"}</h2>
      <label className="field">
        <span>Titel</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={160} />
      </label>
      {!maintenance && (
        <label className="field">
          <span>Ernst</span>
          <select value={severity} onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}>
            <option value="degraded">{COMPONENT_STATUS_LABELS.degraded}</option>
            <option value="outage">{COMPONENT_STATUS_LABELS.outage}</option>
          </select>
        </label>
      )}
      <fieldset className="st-checks">
        <legend>Onderdelen</legend>
        {STATUS_COMPONENTS.map((c) => (
          <label key={c.key} className="check">
            <input
              type="checkbox"
              checked={components.includes(c.key)}
              onChange={(e) => toggle(c.key, e.target.checked)}
            />
            {c.name}
          </label>
        ))}
      </fieldset>
      {maintenance && (
        <>
          <div className="kb-fields">
            <label className="field">
              <span>Begin</span>
              <input
                type="datetime-local"
                value={start}
                min={toLocalInput(new Date())}
                onChange={(e) => setStart(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>Einde</span>
              <input type="datetime-local" value={end} min={start} onChange={(e) => setEnd(e.target.value)} required />
            </label>
          </div>
          {tooSoon && (
            <p className="note" role="status">
              Dit is minder dan {MAINTENANCE_NOTICE_WORKDAYS} werkdagen vooruit. Kondig onderhoud liever eerder aan.
            </p>
          )}
        </>
      )}
      <label className="field">
        <span>{maintenance ? "Wat gebeurt er en wat merkt de klant?" : "Eerste update"}</span>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} required maxLength={5000} />
      </label>
      <label className="check">
        <input type="checkbox" checked={showBanner} onChange={(e) => setShowBanner(e.target.checked)} />
        Banner tonen in de app
      </label>
      <label className="check">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        Volgers mailen
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="btnrow">
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Opslaan…" : maintenance ? "Onderhoud plannen" : "Storing melden"}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Annuleren
        </button>
      </div>
    </form>
  );
}

function IncidentList({
  title,
  empty,
  items,
  onSaved,
}: {
  title: string;
  empty: string;
  items: IncidentDto[];
  onSaved: (d: StatusResponse) => void;
}) {
  return (
    <section className="panel" aria-label={title}>
      <h2>{title}</h2>
      {items.length === 0 && <p className="muted">{empty}</p>}
      {items.map((i) => (
        <Incident key={i.id} incident={i} onSaved={onSaved} />
      ))}
    </section>
  );
}

function Incident({ incident: i, onSaved }: { incident: IncidentDto; onSaved: (d: StatusResponse) => void }) {
  const toast = useToast();
  const last = i.updates[i.updates.length - 1]?.phase ?? "investigating";
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<IncidentPhase>(last);
  const [body, setBody] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const names = i.components.map((k) => STATUS_COMPONENTS.find((c) => c.key === k)?.name ?? k).join(", ");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved(
        await api<StatusResponse>(`/status/incidents/${i.id}/updates`, {
          method: "POST",
          body: { phase, body, notify },
        }),
      );
      setBody("");
      setOpen(false);
      toast("Update geplaatst");
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`st-incident st-${i.status}`} data-testid="st-incident">
      <div className="titlerow">
        <h3>
          {INCIDENT_KIND_LABELS[i.kind]}: {i.title}
        </h3>
        <span className={`chip st-${i.state === "resolved" ? "operational" : i.status}`}>
          {INCIDENT_STATE_LABELS[i.state]}
        </span>
      </div>
      <p className="muted small">
        {names}
        {i.scheduledStart && i.scheduledEnd && (
          <>
            {" · "}
            {fmtDateTime(i.scheduledStart)} tot {fmtDateTime(i.scheduledEnd)}
          </>
        )}
        {i.showBanner && i.state !== "resolved" && " · banner in de app"}
      </p>
      <ol className="st-updates">
        {[...i.updates].reverse().map((u) => (
          <li key={u.id}>
            <strong>{INCIDENT_PHASE_LABELS[u.phase]}</strong>{" "}
            <time className="muted small" dateTime={u.createdAt}>
              {fmtDateTime(u.createdAt)}
            </time>
            <br />
            {u.body}
          </li>
        ))}
      </ol>
      {!open ? (
        <button type="button" className="btn small" onClick={() => setOpen(true)}>
          Update plaatsen
        </button>
      ) : (
        <form onSubmit={(e) => void submit(e)} aria-label={`Update voor ${i.title}`}>
          <label className="field">
            <span>Fase</span>
            <select value={phase} onChange={(e) => setPhase(e.target.value as IncidentPhase)}>
              {INCIDENT_PHASES.map((p) => (
                <option key={p} value={p}>
                  {INCIDENT_PHASE_LABELS[p]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Tekst</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} required maxLength={5000} />
          </label>
          <label className="check">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            Volgers mailen
          </label>
          <div className="btnrow">
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "Plaatsen…" : "Plaatsen"}
            </button>
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              Annuleren
            </button>
          </div>
        </form>
      )}
    </article>
  );
}
